import React, { useState, useEffect, useContext, useRef } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { DirectionContext } from "../App";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";
import { useLocation } from "react-router-dom";

const LawyerVerification = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { navigateTo } = useContext(DirectionContext);
  const location = useLocation();
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthDateBack, setBirthDateBack] = useState("");
  const [fullBirthDate, setFullBirthDate] = useState(""); // 전체 생년월일 (YYYYMMDD) 저장용
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [timer, setTimer] = useState(600); // 10분 (600초)
  const [resendTimer, setResendTimer] = useState(0); // 재전송 타이머
  const [isVerificationRequested, setIsVerificationRequested] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isPendingVerification, setIsPendingVerification] = useState(false); // pendingVerification 데이터가 있을 때 true
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false); // 요청 제한 모달 상태 추가
  const [showNotAllowedModal, setShowNotAllowedModal] = useState(false); // 인증 불가 모달 상태 추가
  const [verificationToken, setVerificationToken] = useState(""); // 인증 토큰 저장
  const [modalType, setModalType] = useState(""); // 모달 타입 상태 추가
  const verificationInputRef = useRef(null);
  const buttonsContainerRef = useRef(null); // 버튼 컨테이너에 대한 ref 추가
  const birthDateBackRef = useRef(null); // 주민번호 뒷자리 ref 추가
  const [representativeName, setRepresentativeName] = useState("");
  const [isLoadingIdentityData, setIsLoadingIdentityData] = useState(false); // 본인인증 데이터 로딩 상태
  const hasFetchedIdentityData = useRef(false); // 본인인증 데이터 조회 여부 추적

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // URL 파라미터에서 identityVerificationId 확인 및 본인인증 데이터 조회
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const identityVerificationId = searchParams.get("identityVerificationId");
    const code = searchParams.get("code");

    // 인증 실패 코드가 있는 경우
    if (code) {
      console.error("본인인증 실패:", code);
      alert("본인인증에 실패했습니다. 다시 시도해주세요.");
      return;
    }

    // 토큰이 있는 경우 (카카오 전자서명으로 인증 완료한 경우)
    const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
    if (token && !hasFetchedIdentityData.current) {
      hasFetchedIdentityData.current = true;
      fetchUserDataFromToken(token);
      return;
    }

    // 쿠키에서 pendingVerification 데이터 확인 (인증수단 없음으로 온 경우)
    const pendingName = Cookies.get("pendingVerificationName");
    const pendingPhone = Cookies.get("pendingVerificationPhone");
    const pendingBirthDate = Cookies.get("pendingVerificationBirthDate");
    const pendingBirthDateFront = Cookies.get(
      "pendingVerificationBirthDateFront",
    );
    const pendingBirthDateBack = Cookies.get(
      "pendingVerificationBirthDateBack",
    );

    if (pendingName && pendingPhone && pendingBirthDate) {
      // 쿠키에서 데이터 가져와서 설정
      setName(pendingName);
      setPhoneNumber(pendingPhone);
      // 주민등록번호 앞자리 설정 (YYMMDD 형식)
      if (pendingBirthDateFront) {
        setBirthDate(pendingBirthDateFront);
      } else {
        // 생년월일 8자리(YYYYMMDD)에서 뒤 6자리(YYMMDD) 추출
        setBirthDate(pendingBirthDate.substring(2, 8)); // 뒤 6자리만
      }
      // 주민등록번호 뒷자리 설정
      if (pendingBirthDateBack) {
        setBirthDateBack(pendingBirthDateBack);
      }
      // pendingVerification 상태로 설정 (토큰은 없지만 수정 불가능하게 함)
      setIsPendingVerification(true);
      // 쿠키 삭제
      Cookies.remove("pendingVerificationName");
      Cookies.remove("pendingVerificationPhone");
      Cookies.remove("pendingVerificationBirthDate");
      Cookies.remove("pendingVerificationBirthDateFront");
      Cookies.remove("pendingVerificationBirthDateBack");
      return;
    }

    // identityVerificationId가 있고, 아직 조회하지 않은 경우에만 조회
    if (identityVerificationId && !hasFetchedIdentityData.current) {
      hasFetchedIdentityData.current = true;
      fetchIdentityVerificationData(identityVerificationId);
    }
  }, [location.search]);

  // 토큰으로 사용자 정보 가져오기
  const fetchUserDataFromToken = async (token) => {
    setIsLoadingIdentityData(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("사용자 정보를 가져오는데 실패했습니다.");
      }

      const data = await response.json();

      // 사용자 정보 자동 입력
      if (data.name) {
        setName(data.name);
        // 이름을 쿠키에 저장 (UserDataCollection에서 예금주명 검증에 사용)
        Cookies.set("verifiedName", data.name, {
          expires: 7,
          path: "/",
        });
        // pendingVerificationName도 저장 (예금주명 검증 우선순위)
        Cookies.set("pendingVerificationName", data.name, {
          expires: 7,
          path: "/",
        });
      }

      if (data.phoneNumber) {
        // 숫자만 추출
        const phoneNumberValue = data.phoneNumber.replace(/[^0-9]/g, "");
        setPhoneNumber(phoneNumberValue);
        // 휴대폰 번호를 쿠키에 저장 (숫자만)
        Cookies.set("verifiedPhoneNumber", phoneNumberValue, {
          expires: 7,
          path: "/",
        });
      }

      if (data.birthDate) {
        // birthDate가 있는 경우 (YYYYMMDD 형식, 예: "19940406")
        const birthDateStr = data.birthDate.toString().replace(/[^0-9]/g, "");
        if (birthDateStr.length >= 8) {
          // 앞 2자리(연도)를 제외하고 6자리(월일) 추출
          // "19940406" -> "940406"
          const birthDateValue = birthDateStr.substring(2, 8);
          setBirthDate(birthDateValue);
          setFullBirthDate(birthDateStr);
        } else if (birthDateStr.length === 6) {
          // 이미 YYMMDD 형식인 경우
          setBirthDate(birthDateStr);
        }
      } else if (data.residentNumber) {
        // birthDate가 없을 때만 residentNumber에서 앞 6자리 추출
        const residentNumberStr = data.residentNumber.toString();
        if (residentNumberStr.length >= 6) {
          const birthDateValue = residentNumberStr.substring(0, 6);
          setBirthDate(birthDateValue);
        }
      }

      // 본인인증 완료 상태로 설정
      setIsVerified(true);
    } catch (error) {
      console.error("사용자 정보 조회 오류:", error);
      setIsLoadingIdentityData(false);
    } finally {
      setIsLoadingIdentityData(false);
    }
  };

  // 본인인증 데이터 조회 함수
  const fetchIdentityVerificationData = async (identityVerificationId) => {
    setIsLoadingIdentityData(true);
    try {
      // 서버 API로 identityVerificationId를 전송하여 본인인증 데이터 조회
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/verification/identity-verifications`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            identityVerificationId: identityVerificationId,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("본인인증 데이터 조회 실패");
      }

      const data = await response.json();

      // 서버 응답 형식에 따라 데이터 추출
      // 서버 응답 형식: { success: boolean, identityVerification: object, isVerified: boolean }
      // identityVerification 내부에 verifiedCustomer가 있거나, 직접 customer 정보가 있을 수 있음
      let customer = null;

      if (data.success && data.isVerified && data.identityVerification) {
        // identityVerification 객체에서 customer 정보 추출
        const identityVerification = data.identityVerification;

        if (identityVerification.verifiedCustomer) {
          // PortOne 원본 형식 (verifiedCustomer 내부)
          customer = identityVerification.verifiedCustomer;
        } else if (identityVerification.name) {
          // 서버에서 가공한 형식 (직접 customer 정보)
          customer = {
            name: identityVerification.name,
            phoneNumber:
              identityVerification.phoneNumber || identityVerification.phone,
            birthDate: identityVerification.birthDate,
            ci: identityVerification.ci,
          };
        }
      } else if (data.status === "VERIFIED" && data.verifiedCustomer) {
        // PortOne 원본 형식 (직접 응답)
        customer = data.verifiedCustomer;
      } else if (data.ok && data.name) {
        // 서버에서 가공한 형식 (직접 응답)
        customer = {
          name: data.name,
          phoneNumber: data.phone || data.phoneNumber,
          birthDate: data.birthDate,
          ci: data.ci,
        };
      }

      if (customer) {
        // 이름 설정
        if (customer.name) {
          setName(customer.name);
          // 이름을 쿠키에 저장 (UserDataCollection에서 예금주명 검증에 사용)
          Cookies.set("verifiedName", customer.name, {
            expires: 7,
            path: "/",
          });
          // pendingVerificationName도 저장 (예금주명 검증 우선순위)
          Cookies.set("pendingVerificationName", customer.name, {
            expires: 7,
            path: "/",
          });
        }

        // 생년월일 설정 (YYYY-MM-DD 형식에서 분리)
        if (customer.birthDate) {
          // YYYY-MM-DD 형식을 YYYYMMDD로 변환
          const birthDateStr = customer.birthDate.replace(/-/g, "");
          if (birthDateStr.length >= 8) {
            // 전체 생년월일 저장 (YYYYMMDD)
            setFullBirthDate(birthDateStr);
            // 앞 6자리 (YYMMDD) - 출생년도 뒤 2자리 + 월 + 일
            const year = birthDateStr.substring(0, 4);
            const month = birthDateStr.substring(4, 6);
            const day = birthDateStr.substring(6, 8);
            setBirthDate(year.substring(2) + month + day);
            // 뒷자리는 사용자가 입력하도록 비워둠
          }
        }

        // 휴대폰 번호 설정 - 그대로 설정 (이름, 주민번호 앞자리처럼)
        const phoneNumberValue =
          customer.phoneNumber ||
          data.identityVerification?.verifiedCustomer?.phoneNumber;

        if (phoneNumberValue) {
          // 숫자만 추출하여 설정
          const phone = String(phoneNumberValue).replace(/[^0-9]/g, "");
          setPhoneNumber(phone);
          // 쿠키에도 숫자만 저장 (NewsProofSubmission에서 검증에 사용)
          Cookies.set("verifiedPhoneNumber", phone, {
            expires: 7,
            path: "/",
          });
        }

        // token을 accessToken으로 저장
        if (data.token) {
          Cookies.set(
            API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN,
            data.token,
            {
              expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.ACCESS_TOKEN,
              ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
            },
          );
        }

        // 본인인증 완료 상태로 설정 (인증번호 입력 없이 완료)
        setIsVerified(true);
        setIsVerificationRequested(false); // 인증번호 요청하지 않음

        // CI 정보를 쿠키에 저장 (필요한 경우)
        if (customer.ci) {
          Cookies.set("identityVerificationCI", customer.ci, {
            expires: 7,
            path: "/",
          });
        }

        // identityVerificationId를 쿠키에 저장
        Cookies.set("identityVerificationId", identityVerificationId, {
          expires: 7,
          path: "/",
        });
      } else {
        throw new Error("본인인증이 완료되지 않았습니다.");
      }
    } catch (error) {
      console.error("본인인증 데이터 조회 오류:", error);
      // 서버 API가 아직 구현되지 않은 경우를 대비하여 개발용 처리
      // 실제로는 서버 API를 구현해야 함
      alert(
        "본인인증 데이터를 가져오는 중 오류가 발생했습니다. 수동으로 입력해주세요.",
      );
    } finally {
      setIsLoadingIdentityData(false);
    }
  };

  // 타이머 기능
  useEffect(() => {
    let interval;
    if (isVerificationRequested && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isVerificationRequested, timer]);

  // 재전송 타이머 기능
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // 타이머 포맷 (분:초)
  const formatTime = () => {
    const minutes = Math.floor(timer / 60);
    const seconds = timer % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // 인증번호 요청 처리
  const handleRequestVerification = async () => {
    if (phoneNumber.length === 11) {
      setIsVerificationRequested(true);
      setVerificationCode(""); // 인증번호 초기화
      setTimer(600); // 타이머 초기화
      setResendTimer(10); // 재전송 타이머 10초로 설정
      setVerificationFailed(false); // 실패 메시지 초기화

      try {
        const applicationType = Cookies.get("applicationType") || "self";
        // API 호출로 인증번호 요청
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/auth/send-otp`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              phoneNumber: phoneNumber,
              applicationType:
                applicationType === "representative"
                  ? "REPRESENTATIVE"
                  : "INDIVIDUAL",
            }),
          },
        );

        if (response.status === 403) {
          navigateTo("/?joint=true");

          setModalType("not-allowed");
          setShowNotAllowedModal(true);
          throw new Error("잘못된 접근입니다.");
        }

        // 429 상태 코드 체크 (요청 제한 초과)
        if (response.status === 429) {
          setIsVerificationRequested(false);
          setModalType("rate-limit");
          setShowRateLimitModal(true);
          throw new Error(
            "요청 제한을 초과했습니다. 잠시 후 다시 시도해주세요.",
          );
        }

        if (response.status !== 201) {
          throw new Error("인증번호 전송에 실패했습니다.");
        }

        // 인증번호 입력 필드에 포커스
        setTimeout(() => {
          if (verificationInputRef.current) {
            verificationInputRef.current.focus();
          }
        }, 100);
      } catch (error) {
        // 요청 제한 초과가 아닌 경우에만 일반 실패 처리
        if (!error.message.includes("요청 제한을 초과")) {
          setVerificationFailed(true);
          setIsVerificationRequested(false);
        }
      }
    }
  };

  // 인증번호 재전송 처리
  const handleResendVerification = async () => {
    setTimer(600); // 타이머 초기화
    setResendTimer(10); // 재전송 타이머 10초로 설정
    setVerificationCode(""); // 인증번호 초기화
    setVerificationFailed(false); // 실패 메시지 초기화

    try {
      const applicationType = Cookies.get("applicationType") || "self";
      // API 호출로 인증번호 재요청
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/auth/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          applicationType:
            applicationType === "representative"
              ? "REPRESENTATIVE"
              : "INDIVIDUAL",
        }),
      });
      if (response.message === "잘못된 접근입니다.") {
        navigateTo("/?joint=true");
      }
      if (response.status === 403) {
        setModalType("not-allowed");
        setShowNotAllowedModal(true);
        throw new Error("잘못된 접근입니다.");
      }

      // 429 상태 코드 체크 (요청 제한 초과)
      if (response.status === 429) {
        setModalType("rate-limit");
        setShowRateLimitModal(true);
        throw new Error("요청 제한을 초과했습니다. 잠시 후 다시 시도해주세요.");
      }

      if (response.status !== 201) {
        throw new Error("인증번호 재전송에 실패했습니다.");
      }

      // 인증번호 입력 필드에 포커스
      setTimeout(() => {
        if (verificationInputRef.current) {
          verificationInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      // 요청 제한 초과가 아닌 경우에만 일반 실패 처리
      if (!error.message.includes("요청 제한을 초과")) {
        setVerificationFailed(true);
      }
    }
  };

  // 인증 검증 처리
  const handleVerify = async () => {
    if (verificationCode.length === 6) {
      try {
        // 인증번호 검증 API 호출
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/auth/verify-otp`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              phoneNumber: phoneNumber,
              code: verificationCode,
            }),
          },
        );

        if (response.status !== 201) {
          throw new Error("인증에 실패했습니다.");
        }

        const data = await response.json();

        if (data.verified) {
          // 토큰 검증 API 호출
          const tokenResponse = await fetch(
            `${API_ENDPOINTS.BASE_URL}/auth/verify-token`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                token: data.token,
              }),
            },
          );

          if (tokenResponse.status !== 201) {
            throw new Error("토큰 검증에 실패했습니다.");
          }

          // 인증 성공 처리
          setIsVerified(true);
          setVerificationFailed(false);
          setVerificationToken(data.token);

          // 토큰과 전화번호를 쿠키에 저장
          Cookies.set(
            API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN,
            data.token,
            {
              expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.ACCESS_TOKEN,
              ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
            },
          );

          // 휴대폰 번호를 숫자만 추출하여 쿠키에 저장 (NewsProofSubmission에서 검증에 사용)
          const normalizedPhone = String(phoneNumber).replace(/[^0-9]/g, "");
          Cookies.set("verifiedPhoneNumber", normalizedPhone, {
            expires: 7,
            ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
          });

          // 사용자 정보 가져오기 (clients/me GET 요청)
          const userInfoResponse = await fetch(
            `${API_ENDPOINTS.BASE_URL}/clients/me`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${data.token}`,
              },
            },
          );

          if (userInfoResponse.ok) {
            const userInfoData = await userInfoResponse.json();

            // 이미 신청 완료한 집단소송이 있는지 확인 (듀오 소송만 체크)
            const hasCompletedApplication =
              userInfoData.applications &&
              userInfoData.applications.some(
                (app) =>
                  app.lawsuitId === 3 && app.status === "PAYMENT_COMPLETED",
              );

            // 전화번호 정규화 (하이픈, 공백 제거)
            const normalizedPhone =
              userInfoData.phoneNumber?.replace(/[\s-]/g, "") || "";

            if (hasCompletedApplication && normalizedPhone !== "01066076676") {
              // 이미 집단소송 신청을 완료한 사용자 (듀오 소송만 체크)
              // 단, 전화번호가 01066076676인 경우는 제외
              setModalType("already-completed");
              setShowModal(true);
              return;
            }
          }

          // coApplicant 상태 확인
          if (data.coApplicant) {
            // 대표신청자 이름 저장
            if (data.coApplicant.representativeName) {
              setRepresentativeName(data.coApplicant.representativeName);
            }

            if (data.coApplicant.status === "PENDING") {
              // 서명 대기 중
              setModalType("signature-pending");
              setShowModal(true);
            } else if (data.coApplicant.status === "VERIFIED") {
              // 서명 완료됨
              setModalType("signature-verified");
              setShowModal(true);
            }
          } else {
            // 인증 완료 후 바로 /clients/me PATCH 호출 후 /user-data로 이동
            try {
              // PATCH 요청 body 구성 (UpdateClientDto 허용 필드만 전송)
              const putBody = {
                name: name,
                phoneNumber: normalizedPhone,
                birthDate:
                  fullBirthDate ||
                  (birthDate.length === 6
                    ? parseInt(birthDate.substring(0, 2)) >= 50
                      ? "19" + birthDate
                      : "20" + birthDate
                    : birthDate),
              };

              // /clients/me PATCH API 호출
              const putResponse = await fetch(
                `${API_ENDPOINTS.BASE_URL}/clients/me`,
                {
                  method: "PATCH",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${data.token}`,
                  },
                  body: JSON.stringify(putBody),
                },
              );

              if (!putResponse.ok) {
                console.error(
                  "사용자 정보 업데이트 실패:",
                  await putResponse.text(),
                );
              } else {
                // 이름을 쿠키에 저장 (UserDataCollection에서 예금주명 검증에 사용)
                if (name) {
                  Cookies.set("verifiedName", name, {
                    expires: 7,
                    path: "/",
                  });
                  // pendingVerificationName도 저장 (예금주명 검증 우선순위)
                  Cookies.set("pendingVerificationName", name, {
                    expires: 7,
                    path: "/",
                  });
                }
                // 성공 시 /user-data로 이동
                navigateTo("/user-data");
                return;
              }
            } catch (error) {
              console.error("사용자 정보 업데이트 중 오류:", error);
            }
          }

          // 인증 완료 후 버튼 컨테이너로 스크롤
          setTimeout(() => {
            buttonsContainerRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
          }, 300);
        } else {
          throw new Error("인증에 실패했습니다.");
        }
      } catch (error) {
        setIsVerified(false);
        setVerificationFailed(true);
      }
    }
  };

  // 다음 단계로 이동
  const handleNext = async () => {
    try {
      // 토큰 가져오기
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);

      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      // 사용자 정보 먼저 가져오기
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("사용자 정보를 가져오는데 실패했습니다.");
      }

      const data = await response.json();

      // 이미 신청 완료한 집단소송이 있는지 확인 (듀오 소송만 체크)
      const hasCompletedApplication =
        data.applications &&
        data.applications.some(
          (app) => app.lawsuitId === 3 && app.status === "PAYMENT_COMPLETED",
        );

      if (hasCompletedApplication) {
        // 이미 집단소송 신청을 완료한 사용자 (듀오 소송만 체크)
        setShowModal(true);
        setModalType("already-completed");
      } else {
        // 신청 완료하지 않은 사용자만 통신사 정보 업데이트
        // 주민등록번호를 문자열로 합치기 (앞 6자리 + 뒤 7자리)
        const residentNumber = birthDate + birthDateBack;

        // 주민등록번호를 쿠키에 저장 (UserDataCollection에서 사용)
        Cookies.set("residentNumber", residentNumber, {
          expires: 7,
          path: "/",
        });

        // 이름을 쿠키에 저장 (UserDataCollection에서 예금주명 검증에 사용)
        // IdentityVerificationInputPage에서 인증수단 없이 넘겨받은 경우도 포함
        if (name) {
          Cookies.set("verifiedName", name, {
            expires: 7,
            path: "/",
          });
          // pendingVerificationName도 저장 (예금주명 검증 우선순위)
          Cookies.set("pendingVerificationName", name, {
            expires: 7,
            path: "/",
          });
        }

        const telecomProviderResponse = await fetch(
          `${API_ENDPOINTS.BASE_URL}/clients/me`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              phoneNumber: phoneNumber,
              name: name,
              birthDate:
                fullBirthDate ||
                (birthDate.length === 6
                  ? parseInt(birthDate.substring(0, 2)) >= 50
                    ? "19" + birthDate
                    : "20" + birthDate
                  : birthDate),
            }),
          },
        );

        if (!telecomProviderResponse.ok) {
          throw new Error("통신사 정보 업데이트에 실패했습니다.");
        }

        // 다음 단계로 진행 - /user-data로 이동
        navigateTo("/user-data");
      }
    } catch (error) {
      // 오류 발생 시 기본 동작으로 identity-verification-input으로 이동
      navigateTo("/identity-verification-input");
    }
  };

  // 모달 확인 버튼
  const handleModalConfirm = () => {
    setShowModal(false);

    // 이미 완료한 사용자인 경우 select 페이지로 이동
    if (modalType === "already-completed") {
      Cookies.remove(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      navigateTo("/select");
    }
    // 다른 타입의 모달은 각각 처리
    else if (modalType === "signature-pending") {
      // 서명 대기 중일 경우 적절한 페이지로 이동
      navigateTo("/select");
    } else if (modalType === "signature-verified") {
      // 서명 완료된 경우 적절한 페이지로 이동
      navigateTo("/select");
    }
    // 다른 통신사 사용자인 경우 모달만 닫고 상태는 유지
  };

  // 요청 제한 모달 닫기
  const handleRateLimitModalClose = () => {
    setShowRateLimitModal(false);
  };

  // 잘못된 접근 모달 닫기
  const handleNotAllowedModalClose = () => {
    setShowNotAllowedModal(false);
  };

  // 아니오 버튼 클릭 시 모달 표시 (함수 중복 제거)
  // const handleOtherCarrier = () => {
  //   setSelectedOption("other");
  //   setShowModal(true);
  // };

  // 모달이 열리면 body에 no-scroll 클래스 추가
  useEffect(() => {
    if (showModal) {
      // 모달이 열렸을 때만 스크롤 방지, 그 외에는 스크롤 허용
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      // 컴포넌트 언마운트 시 스크롤 허용 상태로 복원
      document.body.style.overflow = "auto";
    };
  }, [showModal]);

  // 6자리 입력 시 키보드 닫기
  const handleVerificationCodeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setVerificationCode(value);

    // 6자리 입력 완료 시 자동으로 키보드 닫기
    if (value.length === 6) {
      verificationInputRef.current.blur();
    }
  };
  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <Header>
            <SubTitle>신청인의 정보를 입력해주세요</SubTitle>
            <Description>
              소송 서류 작성에 필요한 정보를 입력해주세요
            </Description>
          </Header>

          {/* 안내문구 */}
          <InfoBox>
            <InfoIcon>ⓘ</InfoIcon>
            <InfoText>
              성함이 일치하지 않으면 신청을 진행할 수 없습니다.
            </InfoText>
          </InfoBox>

          {/* 본인 인증하기 섹션 */}
          <VerificationSection>
            <SectionTitle>본인 인증하기</SectionTitle>
            {isLoadingIdentityData && (
              <LoadingMessage>본인인증 정보를 불러오는 중...</LoadingMessage>
            )}
            <FormGroup>
              <Label>
                이름<Required>*</Required>
              </Label>
              <Input
                type="text"
                placeholder="이름을 입력하세요"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={
                  isLoadingIdentityData || isVerified || isPendingVerification
                }
                readOnly={isVerified || isPendingVerification}
                style={
                  isVerified || isPendingVerification
                    ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" }
                    : {}
                }
              />
            </FormGroup>

            <FormGroup>
              <Label>
                휴대폰 번호<Required>*</Required>
              </Label>
              <PhoneInputContainer>
                <PhoneInput
                  type="tel"
                  placeholder="휴대폰 번호를 입력하세요"
                  value={phoneNumber || ""}
                  onChange={(e) =>
                    !isVerified &&
                    setPhoneNumber(
                      e.target.value.replace(/[^0-9]/g, "").slice(0, 11),
                    )
                  }
                  disabled={isLoadingIdentityData || isVerified}
                  readOnly={isVerified}
                  $hasButton={!isVerified}
                  isNumeric
                  style={
                    isVerified
                      ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" }
                      : {}
                  }
                />
                {!isVerified && (
                  <VerifyButtonWrapper>
                    {phoneNumber.length === 11 ? (
                      isVerificationRequested ? (
                        resendTimer === 0 && !isVerified ? (
                          <VerifyButton onClick={handleResendVerification}>
                            재전송
                          </VerifyButton>
                        ) : (
                          <VerifyButtonDisabled>재전송</VerifyButtonDisabled>
                        )
                      ) : (
                        <VerifyButton onClick={handleRequestVerification}>
                          인증번호 전송
                        </VerifyButton>
                      )
                    ) : (
                      <VerifyButtonDisabled>인증번호 전송</VerifyButtonDisabled>
                    )}
                  </VerifyButtonWrapper>
                )}
              </PhoneInputContainer>
            </FormGroup>

            {isVerificationRequested && !isVerified && (
              <FormGroup>
                <VerificationInputContainer>
                  <VerificationInput
                    type="text"
                    placeholder="인증번호 입력하기"
                    value={verificationCode}
                    onChange={handleVerificationCodeChange}
                    ref={verificationInputRef}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    isNumeric
                  />
                  <VerifyButtonWrapper>
                    {verificationCode.length === 6 && !isVerified ? (
                      <VerifyButton onClick={handleVerify}>
                        인증하기
                      </VerifyButton>
                    ) : (
                      <VerifyButtonDisabled>인증하기</VerifyButtonDisabled>
                    )}
                  </VerifyButtonWrapper>
                </VerificationInputContainer>

                <VerificationHint>
                  인증번호가 오지 않았다면{" "}
                  <ResendHighlight>재전송</ResendHighlight>을 눌러주세요.
                </VerificationHint>

                {verificationFailed && (
                  <VerificationFail>
                    인증에 실패했습니다. 인증번호를 다시 확인해주세요.
                  </VerificationFail>
                )}
              </FormGroup>
            )}

            {/* <FormGroup>
              <Label>
                주민등록번호<Required>*</Required>
              </Label>
              <ResidentNumberContainer>
                <ResidentNumberInput
                  type="text"
                  autoComplete="off"
                  placeholder="생년월일 6자리"
                  value={birthDate}
                  onChange={(e) =>
                    setBirthDate(
                      e.target.value.replace(/[^0-9]/g, "").slice(0, 6)
                    )
                  }
                  maxLength={6}
                  disabled={
                    isLoadingIdentityData || isVerified || isPendingVerification
                  }
                  readOnly={isVerified || isPendingVerification}
                  style={
                    isVerified || isPendingVerification
                      ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" }
                      : {}
                  }
                />
                <ResidentNumberInput
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  placeholder="주민번호 뒷 7자리를 입력하세요"
                  value={
                    birthDateBack.length === 0
                      ? ""
                      : birthDateBack.length === 7
                      ? "•••••••"
                      : birthDateBack.length === 1
                      ? birthDateBack
                      : "•".repeat(birthDateBack.length - 1) +
                        birthDateBack[birthDateBack.length - 1]
                  }
                  onChange={(e) => {
                    if (isVerified || isPendingVerification) return; // 인증 완료 또는 pending 시 수정 불가
                    const inputValue = e.target.value;
                    // 마스킹 문자(•)를 제거하고 숫자만 추출
                    const numericValue = inputValue
                      .replace(/[^0-9]/g, "")
                      .slice(0, 7);

                    // 이전 값과 비교하여 실제로 변경된 경우에만 업데이트
                    if (numericValue !== birthDateBack) {
                      setBirthDateBack(numericValue);
                    }

                    // 7자리 입력 완료 시 키보드 닫기
                    if (numericValue.length === 7) {
                      setTimeout(() => {
                        birthDateBackRef.current?.blur();
                      }, 100);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (isVerified || isPendingVerification) {
                      e.preventDefault();
                      return;
                    }
                    // 숫자 키 입력 시 처리
                    if (/^[0-9]$/.test(e.key)) {
                      // 현재 값이 7자리 미만이면 입력 허용
                      if (birthDateBack.length < 7) {
                        const newValue = birthDateBack + e.key;
                        if (newValue.length <= 7) {
                          setBirthDateBack(newValue);
                          e.preventDefault();
                          if (newValue.length === 7) {
                            setTimeout(() => {
                              birthDateBackRef.current?.blur();
                            }, 100);
                          }
                        }
                      } else {
                        e.preventDefault();
                      }
                    }
                    // 백스페이스 키 처리
                    else if (e.key === "Backspace") {
                      if (birthDateBack.length > 0) {
                        setBirthDateBack(birthDateBack.slice(0, -1));
                      }
                    }
                    // Delete 키 처리
                    else if (e.key === "Delete") {
                      if (birthDateBack.length > 0) {
                        setBirthDateBack(birthDateBack.slice(0, -1));
                      }
                    }
                    // 화살표 키, Tab 키는 허용
                    else if (
                      [
                        "ArrowLeft",
                        "ArrowRight",
                        "ArrowUp",
                        "ArrowDown",
                        "Tab",
                      ].includes(e.key)
                    ) {
                      // 허용
                    }
                    // Ctrl/Cmd + A, C, V 등은 허용
                    else if (e.ctrlKey || e.metaKey) {
                      // 허용
                    }
                    // 그 외의 키는 차단
                    else {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    if (isVerified || isPendingVerification) {
                      e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData("text");
                    const numericValue = pastedText
                      .replace(/[^0-9]/g, "")
                      .slice(0, 7);
                    setBirthDateBack(numericValue);

                    if (numericValue.length === 7) {
                      setTimeout(() => {
                        birthDateBackRef.current?.blur();
                      }, 100);
                    }
                  }}
                  maxLength={7}
                  disabled={
                    isLoadingIdentityData || isVerified || isPendingVerification
                  }
                  readOnly={isVerified || isPendingVerification}
                  style={
                    isVerified || isPendingVerification
                      ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" }
                      : {}
                  }
                  ref={birthDateBackRef}
                />
              </ResidentNumberContainer>
            </FormGroup> */}
          </VerificationSection>
          {isVerified && (
            <VerificationSuccess
              style={{ marginTop: "0px", marginBottom: "20px" }}
            >
              본인인증이 완료되었습니다.
            </VerificationSuccess>
          )}

          <ButtonsContainer ref={buttonsContainerRef}>
            <ActionButton
              onClick={handleNext}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={
                !name ||
                birthDate.length !== 6 ||
                birthDateBack.length !== 7 ||
                phoneNumber.length !== 11 ||
                !isVerified
              }
            >
              다음으로
            </ActionButton>
          </ButtonsContainer>

          {/* 모달 */}
          {showModal && (
            <ModalOverlay>
              <ModalContainer>
                <ModalContent>
                  {modalType === "already-completed" ? (
                    <ModalText>
                      이미 집단소송 신청을 완료하셨습니다.
                      <br />
                      소송 진행 상황은 메인 페이지에서 확인하실 수 있습니다.
                    </ModalText>
                  ) : modalType === "signature-pending" ? (
                    <ModalText>
                      {representativeName ? `${representativeName}님의 ` : ""}
                      공동소송 신청에 대한 서명이 대기 중입니다.
                    </ModalText>
                  ) : modalType === "signature-verified" ||
                    modalType === "not-allowed" ? (
                    <ModalText>
                      {representativeName ? `${representativeName}님의 ` : ""}
                      공동소송 신청에 대한 서명이 완료되었습니다.
                    </ModalText>
                  ) : (
                    <ModalText>
                      <Bold>타 통신사</Bold> 사용자도 신청 가능합니다.
                    </ModalText>
                  )}
                  <ModalButton onClick={handleModalConfirm}>확인</ModalButton>
                </ModalContent>
              </ModalContainer>
            </ModalOverlay>
          )}

          {/* 요청 제한 모달 */}
          {showRateLimitModal && (
            <ModalOverlay>
              <ModalContainer>
                <ModalContent>
                  <ModalTitle>요청 제한 초과</ModalTitle>
                  <ModalText>
                    인증번호 요청이 너무 많습니다.
                    <br />
                    잠시 후 다시 시도해주세요.
                  </ModalText>
                  <ModalButton onClick={handleRateLimitModalClose}>
                    확인
                  </ModalButton>
                </ModalContent>
              </ModalContainer>
            </ModalOverlay>
          )}

          {/* 잘못된 접근 모달 */}
          {showNotAllowedModal && (
            <ModalOverlay>
              <ModalContainer>
                <ModalContent>
                  <ModalTitle>잘못된 접근입니다.</ModalTitle>
                  <ModalText>잘못된 접근입니다.</ModalText>
                  <ModalButton onClick={handleNotAllowedModalClose}>
                    확인
                  </ModalButton>
                </ModalContent>
              </ModalContainer>
            </ModalOverlay>
          )}
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f7f7f7;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #f7f7f7;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f7f7f7;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
    margin-top: 0px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StepIndicator = styled.div`
  display: flex;
  width: 54px;
  height: 29px;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #e31c20;
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  margin-top: 25px;
  align-self: flex-start;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.4;
`;

const InfoMessage = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  align-self: flex-start;
`;

const InfoIcon = styled.span`
  color: #e31c20;
  font-size: 16px;
  font-weight: bold;
`;

const InfoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoTextBold = styled.span`
  color: #4b5563;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
`;
const InfoTextBold2 = styled.span`
  color: #4b5563;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  margin-top: 5px;
`;

const HighlightedText = styled.span`
  color: #dc4b3f;
  font-weight: 800;
`;

const InfoTextRegular = styled.span`
  color: #4b5563;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const InfoBox = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #333;
  line-height: 1.5;
  margin: 0;
  flex: 1;
`;

const VerificationSection = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin: 0 0 8px 0;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 30px;
  box-sizing: border-box;
`;

const FormGroup = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0;
`;

const SubText = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const Label = styled.label`
  display: block;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

const Required = styled.span`
  color: #ff4c4c;
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  height: 56px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: white;
  box-sizing: border-box;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    border-color: #e31c20;
  }
`;

const ResidentNumberContainer = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
`;

const ResidentNumberInput = styled.input`
  width: calc(50% - 4px);
  height: 56px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: white;
  box-sizing: border-box;
  letter-spacing: 6px;
  text-align: center;

  &:first-child {
    margin-right: 8px;
  }

  &::placeholder {
    color: #999;
    letter-spacing: normal;
  }

  &:focus {
    outline: none;
    border-color: #e31c20;
  }
`;

const CertificateSelect = styled.select`
  width: 100%;
  height: 56px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: white;
  box-sizing: border-box;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23333' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 40px;

  &:focus {
    outline: none;
    border-color: #e31c20;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 8px;
  background-color: ${(props) =>
    props.isSelected
      ? props.disabled
        ? "rgba(0, 117, 255, 0.05)"
        : "rgba(0, 117, 255, 0.1)"
      : "white"};
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  transition: all 0.2s ease;
  border: 1px solid ${(props) => (props.isSelected ? "#E31C20" : "#e5e5e5")};
  opacity: ${(props) => (props.disabled && !props.isSelected ? 0.6 : 1)};
  width: 100%;
  box-sizing: border-box;

  &:hover {
    background-color: ${(props) =>
      props.disabled ? "inherit" : "rgba(0, 117, 255, 0.05)"};
  }
`;

const RadioCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.isSelected ? "#E31C20" : "#ccc")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
`;

const RadioInner = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #e31c20;
`;

const RadioLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RadioLabelTitle = styled.div`
  color: #2e3840;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const RadioLabelText = styled.div`
  color: #4b5563;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const PhoneInputContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  box-sizing: border-box;
  padding: 0;
`;

const PhoneInput = styled.input`
  flex: 1;
  height: 56px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 ${(props) => (props.$hasButton ? "100px" : "16px")} 0 16px;
  font-size: 16px;
  background-color: white;
  width: 100%;
  box-sizing: border-box;
  letter-spacing: ${(props) => (props.isNumeric ? "2px" : "normal")};

  &::placeholder {
    color: #999;
    letter-spacing: normal;
  }

  &:focus {
    outline: none;
    border-color: #e31c20;
  }
`;

const VerifyButtonWrapper = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
`;

const VerifyButton = styled.button`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 3px;
  background: #f2f9ff;
  color: #e31c20;
  text-align: right;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 125%;
  border: none;
  cursor: pointer;
  white-space: nowrap;
`;

const VerifyButtonDisabled = styled(VerifyButton)`
  color: #999;
  background: #f5f5f5;
  cursor: default;
`;

const VerificationInputContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  margin-bottom: 8px;
  box-sizing: border-box;
  padding: 0;
`;

const VerificationInput = styled.input`
  flex: 1;
  height: 60px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 100px 0 16px;
  font-size: 16px;
  background-color: white;
  width: 100%;
  box-sizing: border-box;
  letter-spacing: ${(props) => (props.isNumeric ? "2px" : "normal")};

  &::placeholder {
    color: #999;
    letter-spacing: normal;
  }
`;

const VerificationStatus = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 8px;
`;

const VerificationLabel = styled.span`
  flex: 1;
  font-size: 14px;
  color: #333;
`;

const VerificationTimer = styled.span`
  font-size: 14px;
  color: #ff6b6b;
  margin-right: 16px;
`;

const VerifyNowButton = styled.button`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 3px;
  background: #f2f9ff;
  color: #e31c20;
  text-align: right;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 125%;
  border: none;
  cursor: pointer;
`;

const VerificationSuccess = styled.div`
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  background-color: rgba(0, 200, 83, 0.1);
  color: #00c853;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const VerificationFail = styled.div`
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const VerificationHint = styled.p`
  font-size: 14px;
  color: #666;
  margin-top: 8px;
`;

const ResendText = styled.span`
  color: #e31c20;
  cursor: pointer;
  font-weight: 600;
`;

const ResendTextDisabled = styled.span`
  color: #999;
  cursor: default;
  font-weight: 600;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 0px;
  padding: 0 0px;
  background-color: #f7f7f7;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
  }
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  max-width: 400px;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 20px;
  background: #e31c20;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  @media (min-width: 769px) {
    max-width: 430px;
    margin: 0 auto;
  }
`;

// 모달 관련 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  touch-action: none;
`;

const ModalContainer = styled.div`
  width: 90%;
  max-width: 355px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #333;
  text-align: left;
  margin-bottom: 30px;
  line-height: 1.5;
  width: 100%;
`;

const ModalButton = styled.button`
  width: 100%;
  height: 50px;
  background-color: #e31c20;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  align-self: center;
`;

const Bold = styled.span`
  font-weight: 700;
`;

// 추가 스타일 컴포넌트
const ResendHighlight = styled.span`
  color: #fe7156;
  text-decoration: underline;
  font-weight: 600;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
  width: 100%;
`;

// 인증 완료 배지 스타일
const VerifiedBadge = styled.span`
  display: inline-block;
  background-color: #00c853;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 6px;
`;

// 로딩 메시지 스타일
const LoadingMessage = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(0, 117, 255, 0.1);
  color: #e31c20;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

export default LawyerVerification;
